import { apiRequest } from './apiClient';
import {
  getIncomingHouseholdInvite,
  type IncomingHouseholdInvite,
} from './householdService';

export type AppNotification = {
  id: string;
  type: string;
  title: string;
  body: string;
  insightId: string | null;
  data: Record<string, string> | null;
  readAt: string | null;
  createdAt: string;
};

export const INCOMING_INVITE_NOTIFICATION_PREFIX = 'incoming-invite-';

export function isIncomingInviteNotificationId(id: string): boolean {
  return id.startsWith(INCOMING_INVITE_NOTIFICATION_PREFIX);
}

export function notificationFromIncomingInvite(
  invite: IncomingHouseholdInvite,
): AppNotification {
  const household = invite.householdName?.trim() || 'their household';
  return {
    id: `${INCOMING_INVITE_NOTIFICATION_PREFIX}${invite.token}`,
    type: 'household_invite',
    title: `${invite.invitedByName} invited you`,
    body: `Join ${household} on MoneyLens. Open to accept.`,
    insightId: null,
    data: {
      token: invite.token,
      event: 'invite_received',
    },
    readAt: null,
    createdAt: invite.expiresAt,
  };
}

export async function listNotifications(): Promise<{
  unreadCount: number;
  notifications: AppNotification[];
}> {
  return apiRequest('/notifications');
}

/** Inbox list plus any pending household invite (survives tapping Later). */
export async function listNotificationsWithIncomingInvite(): Promise<{
  unreadCount: number;
  notifications: AppNotification[];
}> {
  const [inbox, incoming] = await Promise.all([
    listNotifications().catch(() => ({ unreadCount: 0, notifications: [] as AppNotification[] })),
    getIncomingHouseholdInvite().catch(() => ({ invite: null })),
  ]);

  const invite = incoming.invite;
  if (!invite?.token) return inbox;

  const already = inbox.notifications.some(
    (n) =>
      n.data?.token === invite.token ||
      (n.type === 'household_invite' && n.data?.event === 'invite_received'),
  );
  if (already) return inbox;

  const row = notificationFromIncomingInvite(invite);
  return {
    unreadCount: inbox.unreadCount + 1,
    notifications: [row, ...inbox.notifications],
  };
}

export async function markNotificationRead(id: string): Promise<{ ok: boolean }> {
  return apiRequest(`/notifications/${id}/read`, { method: 'POST' });
}
